import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/App";

const Testimonials = () => {
  const { t } = useTranslation();
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data.testimonials);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
        // Fallback testimonials
        setTestimonials([
          {
            id: "1",
            name: "Sarah M.",
            role: "Marketing Executive, 47",
            quote: "I went from barely functioning at work to having energy until 8pm. The sleep protocol changed everything - I haven't had a 3am wake-up in 6 weeks.",
            outcome: "Energy restored, sleeping through the night"
          },
          {
            id: "2",
            name: "Jennifer K.",
            role: "Entrepreneur, 52",
            quote: "I thought brain fog was just 'my new normal.' Within 3 weeks of following Ivana's nutrition plan, my clarity returned. I'm making decisions with confidence again.",
            outcome: "Brain fog eliminated, decision clarity restored"
          },
          {
            id: "3",
            name: "Lisa R.",
            role: "Healthcare Professional, 49",
            quote: "The emotional toolkit saved my marriage. I finally understood what was happening to my body and could communicate it. My husband says he has his wife back.",
            outcome: "Emotional stability, improved relationships"
          }
        ]);
      }
    };
    fetchTestimonials();
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (testimonials.length === 0) return null;

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="section-padding bg-[#2C3524]"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <span className="overline mb-4 block text-[#DCC286]">{t("testimonials.overline")}</span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white">
            {t("testimonials.title")}
          </h2>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="w-full flex-shrink-0 px-4"
                  data-testid={`testimonial-${testimonial.id}`}
                >
                  <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12">
                    <Quote className="w-12 h-12 text-[#C5A059] mb-6 opacity-50" />
                    <blockquote className="font-heading text-xl md:text-2xl text-white leading-relaxed mb-8">
                      "{testimonial.quote}"
                    </blockquote>
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                        <span className="text-[#C5A059] font-heading text-xl">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-medium">{testimonial.name}</p>
                        <p className="text-white/70 text-sm">{testimonial.role}</p>
                      </div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-white/20">
                      <p className="text-[#C5A059] text-sm font-medium">{t("testimonials.resultLabel")}</p>
                      <p className="text-white/90">{testimonial.outcome}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation */}
          {testimonials.length > 1 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              <button
                onClick={prevTestimonial}
                className="p-3 rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors"
                data-testid="testimonial-prev"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex
                        ? 'bg-[#C5A059] w-8'
                        : 'bg-white/30 hover:bg-white/50'
                    }`}
                    data-testid={`testimonial-dot-${index}`}
                  />
                ))}
              </div>
              <button
                onClick={nextTestimonial}
                className="p-3 rounded-full border border-white/30 text-white hover:bg-white/10 transition-colors"
                data-testid="testimonial-next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
